import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Trash, Plus, CheckCircle, Prohibit } from "@phosphor-icons/react";
import { fmtDate } from "./meta";
import LeadContactFinder from "./LeadContactFinder";

const inp = "bg-surface-2 border-border text-fg h-8 text-xs";

export default function LeadContacts({ siren, contacts, people, searches, blocked, reload }) {
  const [f, setF] = useState({ name: "", role: "", email: "", phone: "", source: "", verified: false, verification_source: "" });
  const [open, setOpen] = useState(false);
  const add = async () => {
    if (f.verified && !f.verification_source.trim()) { toast.error("Indiquez le moyen de vérification pour marquer l'email comme vérifié."); return; }
    try {
      await api.post(`/admin/leads/companies/${siren}/contacts`, f);
      toast.success("Contact ajouté"); setF({ name: "", role: "", email: "", phone: "", source: "", verified: false, verification_source: "" }); setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (cid, oppose) => {
    try { await api.delete(`/admin/leads/companies/${siren}/contacts/${cid}`, { params: { oppose } }); toast.success(oppose ? "Contact supprimé et ajouté à la liste d'opposition" : "Contact supprimé"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const live = (contacts || []).filter((c) => !c.anonymized);
  return (
    <div className="space-y-2" data-testid="lead-contacts">
      {blocked && <p className="text-xs text-amber-500" data-testid="lead-contacts-blocked">Non prospectable : {blocked}.</p>}
      {!live.length && <p className="text-xs text-muted">Aucun contact enregistré. Les emails ne sont jamais déduits ni inventés.</p>}
      {live.map((c) => (
        <div key={c.id} className="flex items-start justify-between gap-2 border border-border rounded-md p-2.5 text-xs" data-testid={`lead-contact-${c.id}`}>
          <div>
            <div className="text-fg font-medium">{c.name || "—"} {c.role && <span className="text-muted font-normal">· {c.role}</span>}</div>
            <div className="text-muted">{c.email || "pas d'email"} {c.phone && `· ${c.phone}`} {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="text-accent ml-1">LinkedIn</a>}</div>
            <div className="text-[10px] text-muted mt-0.5">
              Provenance : {c.source} · ajouté le {fmtDate(c.added_at)}
              {c.verified ? <span className="text-accent ml-1"><CheckCircle size={10} weight="fill" className="inline" /> vérifié ({c.verification_source}, {fmtDate(c.verified_at)})</span> : <span className="ml-1">· non vérifié</span>}
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => del(c.id, true)} title="Droit d'opposition" className="text-muted hover:text-amber-500 p-1" data-testid={`lead-contact-oppose-${c.id}`}><Prohibit size={13} /></button>
            <button onClick={() => del(c.id, false)} title="Supprimer" className="text-muted hover:text-destructive p-1" data-testid={`lead-contact-delete-${c.id}`}><Trash size={13} /></button>
          </div>
        </div>
      ))}
      {open ? (
        <div className="border border-border rounded-md p-3 space-y-2" data-testid="lead-contact-form">
          <div className="grid grid-cols-2 gap-2">
            <Input className={inp} placeholder="Nom" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="lead-contact-name" />
            <Input className={inp} placeholder="Fonction" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="lead-contact-role" />
            <Input className={inp} placeholder="Email professionnel" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="lead-contact-email" />
            <Input className={inp} placeholder="Téléphone professionnel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="lead-contact-phone" />
            <Input className={`${inp} col-span-2`} placeholder="Provenance (ex. site web officiel, salon, carte de visite)" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="lead-contact-source" />
          </div>
          <label className="flex items-center gap-2 text-xs text-fg">
            <input type="checkbox" checked={f.verified} onChange={(e) => setF({ ...f, verified: e.target.checked })} data-testid="lead-contact-verified" /> Email vérifié
          </label>
          {f.verified && <Input className={inp} placeholder="Moyen de vérification (obligatoire)" value={f.verification_source} onChange={(e) => setF({ ...f, verification_source: e.target.value })} data-testid="lead-contact-verification-source" />}
          <div className="flex gap-2">
            <button onClick={add} className="bg-accent text-black text-xs font-semibold rounded px-3 py-1.5" data-testid="lead-contact-save">Ajouter</button>
            <button onClick={() => setOpen(false)} className="text-xs text-muted">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-xs text-accent border border-accent/40 rounded px-2.5 py-1" data-testid="lead-contact-add-btn"><Plus size={12} /> Ajouter un contact</button>
        </div>
      )}
      <LeadContactFinder siren={siren} people={people} searches={searches} disabled={!!blocked} reload={reload} />
    </div>
  );
}
